import { useCallback, useEffect, useState } from 'react'
import type { ClientIndicators, DashboardMode, DashboardTab, DemandCostsResult } from '@/types'
import { clients } from '@/config/clients'
import { OPERATIONS_BOARD_ID } from '@/config/operations'
import { fetchAllClientsCards, fetchBoardAllCards } from '@/services/trello'
import { calculateIndicators, aggregateSummary } from '@/utils/indicators'
import { calculateDemandCosts } from '@/utils/demandCosts'
import { getNextMonth } from '@/utils/dateParser'
import { loadSelectedClientIds, saveSelectedClientIds } from '@/utils/clientSelection'
import { Header } from '@/components/Header'
import { SummaryCards } from '@/components/SummaryCards'
import { ClientTable } from '@/components/ClientTable'
import { ClientFilter } from '@/components/ClientFilter'
import { ClientDetail } from '@/components/ClientDetail'
import { CostOverview } from '@/components/CostOverview'
import { LoadingState } from '@/components/LoadingState'
import { ErrorState } from '@/components/ErrorState'

export default function App() {
  const today = new Date()
  // Operation runs a month ahead: default the dashboard to the month AFTER today.
  const defaultMonth = getNextMonth(today.getMonth(), today.getFullYear())

  // ── State ──────────────────────────────────────────────────────────────────
  const [selectedMonth, setSelectedMonth] = useState(defaultMonth.month)
  const [selectedYear, setSelectedYear] = useState(defaultMonth.year)
  const [mode, setMode] = useState<DashboardMode>('production')
  // "Custos" is a pure UI tab — it never touches `mode` or triggers a refetch.
  const [activeTab, setActiveTab] = useState<DashboardTab>('production')

  const [loading, setLoading] = useState(false)
  const [fatalError, setFatalError] = useState<string | null>(null)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)

  const [indicators, setIndicators] = useState<ClientIndicators[]>([])
  const [selectedClient, setSelectedClient] = useState<ClientIndicators | null>(null)

  const [selectedClientIds, setSelectedClientIds] = useState<Set<string>>(() =>
    loadSelectedClientIds(clients.map((c) => c.boardId))
  )

  const [operationalLoading, setOperationalLoading] = useState(false)
  const [operationalData, setOperationalData] = useState<DemandCostsResult | null>(null)

  // ── Fetch & calculate ──────────────────────────────────────────────────────
  const loadData = useCallback(
    async (month: number, year: number, currentMode: DashboardMode) => {
      setLoading(true)
      setOperationalLoading(true)
      setFatalError(null)

      // Run client cards and operations board in parallel
      const [clientsResult, operationsResult] = await Promise.allSettled([
        clients.length > 0 ? fetchAllClientsCards(clients) : Promise.resolve(new Map()),
        fetchBoardAllCards(OPERATIONS_BOARD_ID),
      ])

      // ── Client indicators (use the selected month) ─────────────────────────
      if (clientsResult.status === 'fulfilled') {
        const resultsMap = clientsResult.value
        const computed = clients.map((client) => {
          const result = resultsMap.get(client.boardId)
          if (!result) {
            return calculateIndicators(client, [], month, year, currentMode, 'Dados não encontrados')
          }
          return calculateIndicators(
            client,
            result.cards,
            month,
            year,
            currentMode,
            result.error ?? (result.listFound ? undefined : `Lista "${client.cronogramaListName}" não encontrada`)
          )
        })
        setIndicators(computed)
      } else {
        const msg =
          clientsResult.reason instanceof Error
            ? clientsResult.reason.message
            : 'Erro desconhecido'
        setFatalError(`Falha ao comunicar com a API: ${msg}`)
      }
      setLoading(false)

      // ── Demand costs (payment cycle named after the selected month) ─────────
      if (operationsResult.status === 'fulfilled') {
        const { cards, customFields, error } = operationsResult.value
        const demandCosts = calculateDemandCosts(cards, customFields, month, year, error)
        setOperationalData(demandCosts)
      } else {
        const msg =
          operationsResult.reason instanceof Error
            ? operationsResult.reason.message
            : 'Erro desconhecido'
        const errDemandCosts = calculateDemandCosts([], [], month, year, msg)
        setOperationalData(errDemandCosts)
      }
      setOperationalLoading(false)

      setLastUpdated(new Date())
    },
    []
  )

  // ── On mount ───────────────────────────────────────────────────────────────
  useEffect(() => {
    loadData(selectedMonth, selectedYear, mode)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Handlers ───────────────────────────────────────────────────────────────
  function handleMonthChange(month: number, year: number) {
    setSelectedMonth(month)
    setSelectedYear(year)
    loadData(month, year, mode)
  }

  function handleTabChange(tab: DashboardTab) {
    setActiveTab(tab)
    // "Custos" reuses the already-loaded operationalData — no refetch, no
    // change to `mode`/calculateIndicators. Only Produção/Agendamento drive
    // the client calculation, exactly as before.
    if ((tab === 'production' || tab === 'scheduling') && tab !== mode) {
      setMode(tab)
      loadData(selectedMonth, selectedYear, tab)
    }
  }

  function handleRefresh() {
    loadData(selectedMonth, selectedYear, mode)
  }

  function handleClientClick(ind: ClientIndicators) {
    setSelectedClient(ind)
  }

  function handleSelectedClientIdsChange(ids: Set<string>) {
    setSelectedClientIds(ids)
    saveSelectedClientIds(ids)
  }

  function handleCloseDetail() {
    setSelectedClient(null)
  }

  // Update selected client when indicators refresh
  useEffect(() => {
    if (selectedClient) {
      const updated = indicators.find(
        (i) => i.client.boardId === selectedClient.client.boardId
      )
      if (updated) setSelectedClient(updated)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indicators])

  // ── Derived ────────────────────────────────────────────────────────────────
  const visibleIndicators = indicators.filter((i) => selectedClientIds.has(i.client.boardId))
  const summary = aggregateSummary(visibleIndicators)

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#f5f3f1]">
      <Header
        selectedMonth={selectedMonth}
        selectedYear={selectedYear}
        activeTab={activeTab}
        loading={loading || operationalLoading}
        onMonthChange={handleMonthChange}
        onTabChange={handleTabChange}
        onRefresh={handleRefresh}
        lastUpdated={lastUpdated}
      />

      <main className="max-w-screen-xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {activeTab === 'costs' ? (
          /* ── Custos — exclusive home of Design/Vídeo demand costs ────────── */
          <CostOverview
            data={operationalData}
            loading={operationalLoading}
            productionMonth={selectedMonth}
            productionYear={selectedYear}
          />
        ) : (
          /* ── Produção / Agendamento — client metrics only ─────────────────── */
          <>
            {/* Client filter */}
            {clients.length > 0 && (
              <ClientFilter
                clients={clients}
                selectedIds={selectedClientIds}
                onChange={handleSelectedClientIdsChange}
              />
            )}

            {/* No clients configured */}
            {clients.length === 0 && !operationalLoading && (
              <div className="card p-10 text-center">
                <p className="text-brand-taupe text-sm">
                  Nenhum cliente configurado. Edite{' '}
                  <code className="bg-brand-beige px-1.5 py-0.5 rounded text-brand-black font-mono text-xs">
                    src/config/clients.ts
                  </code>{' '}
                  para adicionar seus clientes.
                </p>
              </div>
            )}

            {/* Fatal error */}
            {fatalError && !loading && (
              <ErrorState message={fatalError} onRetry={handleRefresh} />
            )}

            {/* Loading skeleton for client section */}
            {loading && <LoadingState />}

            {/* Client content */}
            {!loading && !fatalError && clients.length > 0 && (
              <>
                <SummaryCards
                  total={summary.total}
                  done={summary.done}
                  pending={summary.pending}
                  percentage={summary.percentage}
                  mode={mode}
                />

                {summary.total === 0 && visibleIndicators.length > 0 && (
                  <div className="card p-8 text-center text-brand-taupe text-sm">
                    Nenhum card encontrado para o mês selecionado em nenhum cliente.
                  </div>
                )}

                <ClientTable
                  indicators={visibleIndicators}
                  mode={mode}
                  onClientClick={handleClientClick}
                />
              </>
            )}
          </>
        )}
      </main>

      {/* Client detail modal */}
      {selectedClient && (
        <ClientDetail
          indicators={selectedClient}
          mode={mode}
          onClose={handleCloseDetail}
        />
      )}
    </div>
  )
}
